import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import BloodPressureFlow from "@/components/BloodPressureFlow";
import GlucoseMeterFlow from "@/components/GlucoseMeterFlow";
import PulseOximeterFlow from "@/components/PulseOximeterFlow";
import WeightScaleFlow from "@/components/WeightScaleFlow";
// import {Separator} from "@/components/ui/separator"
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/use-auth";


const ResolveAlert = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const alert = state?.alert;
  const {profile: profileFromAuth, user} = useAuth();
  
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(true);
  const [isOkClicked, setIsOkClicked] = useState(false);
  const [currentFlow, setCurrentFlow] = useState<string | null>(null);

  const internalNoteAuthorName = useMemo(
    () =>
      profileFromAuth?.name ||
      [profileFromAuth?.firstName, profileFromAuth?.lastName]
        .filter(Boolean)
        .join(" ") ||
      user?.name ||
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      "",
    [profileFromAuth, user],
  );

  useEffect(() => {
    if (!alert) {
      toast.error("No alert data found");
      navigate("/alerts");
    }
  }, [alert, navigate]);

  // Remove pointer-events: none from body when landing (can be left by Dialog/Dropdown from previous page)
  useEffect(() => {
    document.body.style.removeProperty("pointer-events");
    return () => {
      document.body.style.removeProperty("pointer-events");
    };
  }, []);

  // Handle Enter key for Note dialog OK button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && isNoteDialogOpen && !e.shiftKey) {
        e.preventDefault();
        handleNoteDialogOk();
      }
    };

    if (isNoteDialogOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isNoteDialogOpen]);

  const handleNoteDialogClose = (open: boolean) => {
    if (!open) {
      // If dialog is being closed and OK was not clicked, navigate back
      if (!isOkClicked) {
        navigate("/alerts");
      } else {
        // OK was clicked, just close the dialog
        setIsNoteDialogOpen(false);
      }
    }
  };

  const handleNoteDialogOk = () => {
    // User clicked OK, start device-specific flow
    setIsOkClicked(true);
    setIsNoteDialogOpen(false);
    
    // Start device-specific flow
    const deviceType = alert?.deviceType;
    if (deviceType === "blood_pressure") {
      setCurrentFlow("blood_pressure");
    } else if (deviceType === "glucose_meter") {
      setCurrentFlow("glucose_meter");
    } else if (deviceType === "pulse_oximeter") {
      setCurrentFlow("pulse_oximeter");
    } else if (deviceType === "weight_scale") {
      setCurrentFlow("weight_scale");
    }
  };
  const handleFlowComplete = () => {
    const patientId = alert?.patientId?._id as string | undefined;
    if (patientId) {
      navigate(`/patients/${patientId}?tab=basic-info&openInternalNote=1`, {
        replace: true,
        state: {},
      });
      return;
    }
    navigate("/alerts", { replace: true });
  };

  if (!alert) {
    return null;
  }

  return (
    <DashboardLayout activePage="/alerts">
      {/* Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={handleNoteDialogClose}>
        <DialogContent 
          className="max-w-lg border-0 p-0 pb-6 px-6"
          onPointerDownOutside={() => {
            // Allow closing on outside click - override default preventDefault
            // The onOpenChange handler will handle navigation
          }}
          onInteractOutside={() => {
            // Allow closing on outside click - override default preventDefault
            // The onOpenChange handler will handle navigation
          }}
        >
          <div className="space-y-6 pt-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Note</h3>
              <div className="border-b" style={{ borderColor: '#ddd' }}></div>
            </div>
            <p className="text-base font-medium text-foreground leading-relaxed">
              Triage is a useful process because it can help prevent a medical emergency and it also allows us to provide real-time patient education. These triage calls are important because the patient can begin to connect their behavior and choices to the elevated readings. These triage calls also help us identify if the patient is experiencing a true medical emergency.
            </p>
            <div className="flex justify-end pt-4">
              <Button onClick={handleNoteDialogOk} className="bg-primary hover:bg-primary/90 text-white">
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/alerts")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <CardTitle>Resolve Alert</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Alert Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Alert Information</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Patient ID</Label>
                    <p className="text-sm font-medium">
                      {alert?.patientId?.patientId || alert?.patientId?._id || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Patient Name</Label>
                    <p className="text-sm font-medium">
                      {`${alert?.patientId?.firstName || ""} ${alert?.patientId?.middleName || ""} ${alert?.patientId?.lastName || ""}`.trim() || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Phone</Label>
                    <p className="text-sm font-medium">
                      {alert?.patientId?.primaryNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Email</Label>
                    <p className="text-sm font-medium">
                      {alert?.patientId?.email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Organization</Label>
                    <p className="text-sm font-medium">
                      {alert?.organizationId?.organizationName || alert?.organizationId || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Device Type</Label>
                    <p className="text-sm font-medium">
                      {alert?.deviceType?.replace(/_/g, " ") || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Status</Label>
                    <p className="text-sm font-medium">
                      {alert?.status?.replace(/_/g, " ") || "N/A"}
                    </p>
                  </div>
                  

<div>
  <Label className="text-sm ">
    Reading Values
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Info style={{ color: "#FFA500" }} className="ml-1 w-4 cursor-pointer " />
        </span>
      </TooltipTrigger>
      <TooltipContent arrowColor="bg-gray-200 fill-gray-200" className="bg-gray-200 text-primary-foreground" >
        <p className="font-medium text-orange-600">
          *{alert?.criticalReason || "N/A"}
        </p>
      </TooltipContent>
    </Tooltip>
  </Label>
  <p className="text-sm font-medium">
    {Object?.entries(alert?.readingValues)
      ?.map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`)
      .join(" , ") || "N/A"}
  </p>
</div>
                </div>

                {/* <Separator /> */}

              </div>

              {/* Device Type Flow Components - Show based on flag */}
              {currentFlow === "blood_pressure" && (
                <BloodPressureFlow
                  isOpen={true}
                  onComplete={handleFlowComplete}
                />
              )}
              
              {currentFlow === "glucose_meter" && (
                <GlucoseMeterFlow
                  isOpen={true}
                  onComplete={handleFlowComplete}
                  alert={alert}
                />
              )}
              
              {currentFlow === "pulse_oximeter" && (
                <PulseOximeterFlow
                  isOpen={true}
                  onComplete={handleFlowComplete}
                />
              )}
              
              {currentFlow === "weight_scale" && (
                <WeightScaleFlow
                  isOpen={true}
                  onComplete={handleFlowComplete}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ResolveAlert;

